import { ReactComponent as FrontLogoIcon } from '@/assets/images/svg/logos/front/front-logo-icon.svg';
import PrimaryInnerGlowButton from '@/components/buttons/primary';
import { VisuallyHidden } from '@/components/Helpers';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import Star from '@/components/Star';
import ComparisonTable from '@/components/tables/ComparisonTable';
import { Column as ComparisonTableColumn } from '@/components/tables/ComparisonTable/components';
import { ComparisonPageQuery, ComparisonPageQueryVariables, ComparisonPathsQuery } from '@/graphql';
import ComparisonPageQueryDocument from '@/graphql/queries/ComparisonPage.gql';
import ComparisonPathsQueryDocument from '@/graphql/queries/ComparisonPaths.gql';
import { getSingleLink } from '@/lib/craft-utils';
import CraftClient from '@/lib/graphql';
import gtm from '@/lib/gtm';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { parseHeading, parseHtml } from '@/lib/parse-html';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { addElementIdentifier } from '@/lib/utils';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { blackScrollbars } from '@/styles/components/scrollbars';
import {
  applyBreakpointNegativeMargins,
  applyBreakpointPadding,
  BREAKPOINT_PADDING_VAR,
  mediaBreakpointOnly,
  mediaBreakpointUp,
  paddingY,
  square,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { useEffect, useState } from 'react';
import striptags from 'striptags';
import styled, { css } from 'styled-components';

// #region - Components

const PageWrapper = styled.section`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 20)};
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(10)};
  overflow: hidden;

  ${mediaBreakpointUp('tablet')} {
    row-gap: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(60)};
  }
`;

const HeroSection = styled.header`
  ${paddingY(40)}
  display: grid;
  grid-template-columns: auto;
  grid-auto-rows: auto;
  row-gap: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(46, 18)}
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content min-content 1fr;
    column-gap: ${remCalc(20)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(47)}
    row-gap: ${remCalc(30)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingY(47, 81)}
  }
`;

const HeroHeading = styled.h1`
  ${customTextSize(24, 30)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  letter-spacing: -0.01em;

  span:last-child {
    display: block;
    background: linear-gradient(90.5deg, ${COLORS.BLUE.LOWLIGHT} -9.81%, ${COLORS.GREEN.BASE} 126.46%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }

  small {
    display: block;
    ${customTextSize(12, 15)}
    font-weight: ${FONT_WEIGHTS.BOLD};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${COLORS.GRAY.BASE};
    margin-bottom: ${remCalc(15)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(22, 28)}
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 38)}

    small {
      ${customTextSize(16, 20)}
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(49, 58)}
  }
`;

const HeroImageWrapper = styled.div`
  ${mediaBreakpointUp('tablet')} {
    grid-column: 2;
    grid-row: 1 / -1;
    margin-top: ${remCalc(37)};
  }

  ${mediaBreakpointUp('desktop')} {
    margin-top: ${remCalc(27)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-top: ${remCalc(17)};
  }
`;

const HeroText = styled.div`
  ${customTextSize(14, 20)}

  p + p {
    margin-top: ${remCalc(16)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(16, 24)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(24, 34)}
  }
`;

const HighlightsSection = styled.section`
  background-image: url('/staticwebassets/pages/compare/background-1.jpg');
  background-size: cover;
  background-position: 40%;
  background-repeat: no-repeat;

  ${mediaBreakpointOnly('tablet')} {
    padding-bottom: ${remCalc(65 - 16)};
  }
`;

const HighlightCard = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(15)};

  background: ${hexToRGBA(COLORS.WHITE, 0.8)};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(30)};

  ${customTextSize(13, 17)}

  h4 {
    ${customTextSize(16, 21)}
    font-weight: ${FONT_WEIGHTS.BOLD};
    color: ${COLORS.BLUE.LOWLIGHT};
  }

  ${mediaBreakpointOnly('tablet')} {
    scroll-snap-align: center;
    scroll-snap-stop: always;
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 24)}

    h4 {
      ${customTextSize(24, 34)}
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(18, 26)}
  }
`;

const HighlightsSectionContent = styled.div`
  ${paddingY(54)}

  > div {
    display: grid;
    grid-auto-rows: auto;
    grid-auto-flow: row;
    gap: ${remCalc(20)};
  }

  ${mediaBreakpointOnly('tablet')} {
    ${paddingY(65)}

    overflow: auto;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
    padding-bottom: ${remCalc(16)};

    ${applyBreakpointNegativeMargins}
    ${applyBreakpointPadding}
    ${blackScrollbars}

    > div {
      width: 150vw;
    }
  }

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;

    > div {
      grid-auto-columns: minmax(0, 1fr);
      grid-auto-flow: column;
      grid-template-rows: auto;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(85)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingY(88)}
  }
`;

const HighlightCardIcon = styled.div`
  ${square(50)}

  img {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    ${square(80)}
  }
`;

const ComparisonSection = styled.section`
  ${paddingY(51, 21)}

  h2 {
    ${customTextSize(32, 38)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.RED.LOWLIGHT};
    margin-bottom: ${remCalc(40)};
    letter-spacing: -0.01em;
  }

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(54)}
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(67)}

    ${Container} {
      display: grid;
      grid-template-columns: repeat(18, 1fr);
      column-gap: ${remCalc(20)};

      > * {
        grid-column: 2 / -2;
      }
    }

    h2 {
      ${customTextSize(49, 58)}
      margin-bottom: ${remCalc(60)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingY(84)}
  }
`;

const ComparisonTableWrapper = styled.div`
  padding: ${remCalc(27)};
  border-radius: ${remCalc(10)};

  background-image: linear-gradient(
      180.47deg,
      rgba(255, 255, 255, 0) 22.94%,
      rgba(255, 255, 255, 0.121527) 29.47%,
      rgba(255, 255, 255, 0.231704) 35.03%,
      rgba(255, 255, 255, 0.331341) 39.74%,
      rgba(255, 255, 255, 0.421249) 43.68%,
      rgba(255, 255, 255, 0.502239) 46.97%,
      rgba(255, 255, 255, 0.575121) 49.7%,
      rgba(255, 255, 255, 0.640707) 51.97%,
      rgba(255, 255, 255, 0.699806) 53.89%,
      rgba(255, 255, 255, 0.753231) 55.56%,
      rgba(255, 255, 255, 0.801791) 57.08%,
      rgba(255, 255, 255, 0.846297) 58.55%,
      rgba(255, 255, 255, 0.88756) 60.07%,
      rgba(255, 255, 255, 0.926392) 61.75%,
      rgba(255, 255, 255, 0.963601) 63.68%,
      #ffffff 65.97%
    ),
    radial-gradient(
        369.18% 52.86% at 125.38% -9.1%,
        rgba(0, 191, 255, 0.4) 16.29%,
        rgba(31, 199, 255, 0.351389) 22.98%,
        rgba(59, 206, 255, 0.307319) 28.68%,
        rgba(84, 212, 255, 0.267464) 33.51%,
        rgba(107, 218, 255, 0.231501) 37.55%,
        rgba(128, 223, 255, 0.199105) 40.91%,
        rgba(147, 228, 255, 0.169952) 43.71%,
        rgba(163, 232, 255, 0.143717) 46.04%,
        rgba(178, 236, 255, 0.120077) 48.01%,
        rgba(192, 239, 255, 0.0987077) 49.71%,
        rgba(204, 242, 255, 0.0792837) 51.27%,
        rgba(216, 245, 255, 0.0614812) 52.78%,
        rgba(226, 248, 255, 0.0449758) 54.34%,
        rgba(236, 250, 255, 0.0294434) 56.06%,
        rgba(246, 253, 255, 0.0145595) 58.04%,
        rgba(255, 255, 255, 0) 60.39%
      )
      /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
    linear-gradient(
      164.82deg,
      rgba(255, 250, 221, 0.5) 28.26%,
      rgba(224, 233, 216, 0.439236) 34.51%,
      rgba(196, 218, 211, 0.384148) 39.83%,
      rgba(171, 204, 207, 0.33433) 44.32%,
      rgba(148, 191, 203, 0.289376) 48.09%,
      rgba(127, 180, 199, 0.248881) 51.23%,
      rgba(108, 169, 196, 0.21244) 53.84%,
      rgba(92, 160, 193, 0.179647) 56.02%,
      rgba(77, 152, 191, 0.150097) 57.85%,
      rgba(63, 145, 189, 0.123385) 59.44%,
      rgba(51, 138, 187, 0.0991046) 60.9%,
      rgba(39, 132, 185, 0.0768514) 62.3%,
      rgba(29, 126, 183, 0.0562198) 63.76%,
      rgba(19, 120, 181, 0.0368042) 65.36%,
      rgba(9, 115, 180, 0.0181994) 67.21%,
      rgba(0, 110, 178, 0) 69.4%
    ),
    linear-gradient(
      19.03deg,
      #acf8e2 5.5%,
      #b6f8e1 17.45%,
      #bff8e1 27.63%,
      #c8f9e0 36.23%,
      #cff9e0 43.44%,
      #d6f9df 49.45%,
      #dcf9df 54.44%,
      #e1f9df 58.6%,
      #e6f9df 62.11%,
      #ebfade 65.16%,
      #effade 67.94%,
      #f2fade 70.62%,
      #f6fade 73.41%,
      #f9fadd 76.48%,
      #fcfadd 80.01%,
      #fffadd 84.21%
    );
  background-size: 100%;

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(40, 28, 60)};
    background-image: linear-gradient(
        91.03deg,
        rgba(255, 255, 255, 0) 16.55%,
        rgba(255, 255, 255, 0.121527) 23.33%,
        rgba(255, 255, 255, 0.231704) 29.11%,
        rgba(255, 255, 255, 0.331341) 34%,
        rgba(255, 255, 255, 0.421249) 38.09%,
        rgba(255, 255, 255, 0.502239) 41.5%,
        rgba(255, 255, 255, 0.575121) 44.34%,
        rgba(255, 255, 255, 0.640707) 46.7%,
        rgba(255, 255, 255, 0.699806) 48.69%,
        rgba(255, 255, 255, 0.753231) 50.42%,
        rgba(255, 255, 255, 0.801791) 52%,
        rgba(255, 255, 255, 0.846297) 53.52%,
        rgba(255, 255, 255, 0.88756) 55.1%,
        rgba(255, 255, 255, 0.926392) 56.84%,
        rgba(255, 255, 255, 0.963601) 58.85%,
        #ffffff 61.23%
      ),
      radial-gradient(
          81.76% 110.27% at 63.72% -6.46%,
          rgba(0, 191, 255, 0.4) 16.29%,
          rgba(31, 199, 255, 0.351389) 22.98%,
          rgba(59, 206, 255, 0.307319) 28.68%,
          rgba(84, 212, 255, 0.267464) 33.51%,
          rgba(107, 218, 255, 0.231501) 37.55%,
          rgba(128, 223, 255, 0.199105) 40.91%,
          rgba(147, 228, 255, 0.169952) 43.71%,
          rgba(163, 232, 255, 0.143717) 46.04%,
          rgba(178, 236, 255, 0.120077) 48.01%,
          rgba(192, 239, 255, 0.0987077) 49.71%,
          rgba(204, 242, 255, 0.0792837) 51.27%,
          rgba(216, 245, 255, 0.0614812) 52.78%,
          rgba(226, 248, 255, 0.0449758) 54.34%,
          rgba(236, 250, 255, 0.0294434) 56.06%,
          rgba(246, 253, 255, 0.0145595) 58.04%,
          rgba(255, 255, 255, 0) 60.39%
        )
        /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
      linear-gradient(
        118.43deg,
        rgba(255, 250, 221, 0.5) 43.28%,
        rgba(224, 233, 216, 0.439236) 56.91%,
        rgba(196, 218, 211, 0.384148) 68.54%,
        rgba(171, 204, 207, 0.33433) 78.36%,
        rgba(148, 191, 203, 0.289376) 86.6%,
        rgba(127, 180, 199, 0.248881) 93.46%,
        rgba(108, 169, 196, 0.21244) 99.15%,
        rgba(92, 160, 193, 0.179647) 103.9%,
        rgba(77, 152, 191, 0.150097) 107.91%,
        rgba(63, 145, 189, 0.123385) 111.39%,
        rgba(51, 138, 187, 0.0991046) 114.56%,
        rgba(39, 132, 185, 0.0768514) 117.63%,
        rgba(29, 126, 183, 0.0562198) 120.81%,
        rgba(19, 120, 181, 0.0368042) 124.31%,
        rgba(9, 115, 180, 0.0181994) 128.35%,
        rgba(0, 110, 178, 0) 133.14%
      ),
      linear-gradient(
        19.03deg,
        #acf8e2 5.5%,
        #b6f8e1 17.45%,
        #bff8e1 27.63%,
        #c8f9e0 36.23%,
        #cff9e0 43.44%,
        #d6f9df 49.45%,
        #dcf9df 54.44%,
        #e1f9df 58.6%,
        #e6f9df 62.11%,
        #ebfade 65.16%,
        #effade 67.94%,
        #f2fade 70.62%,
        #f6fade 73.41%,
        #f9fadd 76.48%,
        #fcfadd 80.01%,
        #fffadd 84.21%
      );
  }

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(60, 50)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: ${remCalc(60)};
  }
`;

const StyledComparisonTable = styled(ComparisonTable)`
  ${ComparisonTableColumn}[data-first-column] {
    border-color: ${COLORS.GREEN.BASE};
  }

  ${mediaBreakpointUp('tablet')} {
    ${ComparisonTableColumn} {
      &[data-first-column] {
        border-color: ${COLORS.GREEN.BASE};
        border-right-color: #a4ccc0;
      }
    }
  }
`;

const ComparisonTableHeader = styled.div`
  ${customTextSize(14, 20)};

  h4 {
    ${customTextSize(24, 30)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    letter-spacing: -0.01em;
    margin-bottom: ${remCalc(18)};
  }

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(20)};
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(20)};
    ${customTextSize(18, 26)};

    h4 {
      ${customTextSize(38, 46)};
    }
  }
`;

const ComparisonTableLogo = styled.div`
  ${square(48)}
  margin-bottom: ${remCalc(20)};
  display: flex;
  align-items: center;
  justify-content: flex-start;

  > div {
    width: 100%;
  }

  svg {
    max-width: 100%;
    max-height: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    ${square(65)}
  }
`;

const FeaturesSection = styled.section`
  position: relative;
  ${paddingY(44, 55)}

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(44, 74)}

    ${Container} {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(64, 77)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingY(64, 101)}
  }
`;

const FeaturesHeadline = styled.div`
  ${customTextSize(14, 20)}
  margin-bottom: ${remCalc(50)};
  text-align: center;

  h2 {
    ${customTextSize(24, 30)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.GREEN.LOWLIGHT};
    margin-bottom: ${remCalc(30)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(16, 24)}
    margin-bottom: ${remCalc(93)};

    grid-column: 2 / -2;
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)}
    margin-bottom: ${remCalc(66)};

    h2 {
      ${customTextSize(49, 58)}
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(60)};

    grid-column: 3 / -3;
  }
`;

const FeatureImage = styled.div`
  position: relative;
  display: block;
  height: 0;
  padding-bottom: ${(186 / 259) * 100}%;
  margin-bottom: ${remCalc(47)};

  box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  overflow: hidden;

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }

  ${mediaBreakpointOnly('desktop')} {
    margin-top: ${remCalc(54)};
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(32)};
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: auto;

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: 1fr 1fr;
    column-gap: ${remCalc(20)};

    grid-column: 1 / -1;

    ${FeatureImage} {
      grid-row: 1;
      grid-column: 2;
    }

    ${FeatureList} {
      grid-row: 1;
      grid-column: 1;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(12, 1fr);

    ${FeatureList} {
      grid-column: 1 / span 5;
    }

    ${FeatureImage} {
      grid-column: 7 / span 6;
    }
  }
`;

const FeatureListItemContent = styled.div`
  display: none;
  ${customTextSize(14, 20)}

  p + p {
    margin-top: ${remCalc(18)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 24)}
  }
`;

const FeatureListItem = styled.li<{ isActive: boolean }>`
  border-left: ${remCalc(4)} solid transparent;
  padding-left: ${remCalc(12)};

  button {
    appearance: none;
    outline: none;
    border: none;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(18, 22)}
    margin-bottom: ${remCalc(10)};
    padding: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  ${(props) =>
    props.isActive &&
    css`
      border-color: ${COLORS.BLUE.BASE};

      ${FeatureListItemContent} {
        display: block;
      }
    `}

  ${mediaBreakpointUp('desktopWide')} {
    padding-left: ${remCalc(18)};

    button {
      ${customTextSize(24, 29)}
    }
  }
`;

const MidPageCTASection = styled.section`
  ${Container} {
    ${paddingY(30)}
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const QuoteSection = styled.section`
  ${paddingY(39, 54)}

  ${Container} {
    position: relative;

    &::before,
    &::after {
      content: '';
      z-index: -1;
    }

    &::before {
      position: absolute;
      width: 322px;
      height: 322px;
      right: -60px;
      top: -100px;

      background: ${COLORS.GREEN.BASE};
      opacity: 0.2;
      filter: blur(70px);
    }

    &::after {
      position: absolute;
      width: 402px;
      height: 402px;
      left: -60px;
      bottom: -100px;

      background: ${COLORS.BLUE.BASE};
      opacity: 0.2;
      filter: blur(70px);
    }
  }

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(77, 127)}

    ${Container} {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
    }
  }
`;

const QuoteLogo = styled.div<{ small?: boolean }>`
  img {
    width: auto;
    max-height: ${(props) => (!!props.small ? 40 : 35)}px;
  }
`;

const LargeQuote = styled.blockquote`
  position: relative;
  ${customTextSize(25, 29)}
  letter-spacing: -0.01em;
  padding-left: ${remCalc(19)};

  &::before {
    content: '“';
    position: absolute;
    top: 7px;
    left: -2px;
    color: ${COLORS.ORANGE.LOWLIGHT};
    font-size: 180%;
  }

  footer {
    margin-top: ${remCalc(30)};
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: ${remCalc(20)};
  }

  ${QuoteLogo} {
    margin-top: ${remCalc(20)};
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 2 / -2;

    footer {
      margin-top: ${remCalc(40)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(40, 47)}
    padding-left: ${remCalc(26)};

    &::before {
      font-size: 150%;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(49, 58)}

    &::before {
      font-size: 140%;
    }
  }
`;

const LargeQuoteImage = styled.div`
  ${square(73)}
  border-radius: 100%;
  overflow: hidden;

  ${mediaBreakpointUp('tablet')} {
    ${square(115)}
  }
`;

const QuoteAuthor = styled.p`
  ${customTextSize(14, 20)}

  span {
    display: block;
  }

  strong {
    color: ${COLORS.ORANGE.LOWLIGHT};
  }

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(16, 24)}

    strong {
      ${customTextSize(20, 25)}
    }
  }

  ${mediaBreakpointUp('desktop')} {
    &,
    strong {
      ${customTextSize(24, 30)}
    }
  }
`;

const SecondaryQuotesSection = styled.section`
  background-color: ${COLORS.GRAY.TINT};
  margin-bottom: ${remCalc(180 + 50)};

  ${Container} {
    position: relative;
    ${paddingY(41, 58)}
  }

  h2 {
    ${customTextSize(24, 30)}
    letter-spacing: -0.01em;
    color: ${COLORS.BLUE.LOWLIGHT};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    margin-bottom: ${remCalc(32)};
  }

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(63)};

    ${Container} {
      ${paddingY(58, 69)}
      display: grid;
      column-gap: ${remCalc(20)};
      grid-template-columns: repeat(12, 1fr);
    }

    h2 {
      grid-column: 1 / span 7;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(85)};

    ${Container} {
      ${paddingY(77, 85)}
    }

    h2 {
      ${customTextSize(38, 46)}
      margin-bottom: ${remCalc(60)};
      grid-column: 1 / span 8;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(125)};

    h2 {
      grid-column: 1 / span 6;
    }
  }
`;

const SmallQuote = styled.blockquote`
  position: relative;
  ${customTextSize(16, 24)}
  padding-left: ${remCalc(24)};

  &::before {
    content: '“';
    position: absolute;
    top: 9px;
    left: 0;
    color: ${COLORS.BLUE.LOWLIGHT};
    font-size: 275%;
  }

  & + & {
    margin-top: ${remCalc(40)};
  }

  footer {
    margin-top: ${remCalc(20)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  ${mediaBreakpointUp('tablet')} {
    grid-column: 2 / span 7;
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)}
    padding-left: ${remCalc(26)};

    &::before {
      font-size: 225%;
    }
  }
`;

const QuoteIllustrationTop = styled.div`
  position: absolute;
  width: 152px;
  top: -122px;
  right: ${BREAKPOINT_PADDING_VAR};

  img {
    width: 100%;
  }

  ${mediaBreakpointOnly('mobile')} {
    display: none;
  }

  ${mediaBreakpointUp('desktop')} {
    width: 201px;
    top: -151px;
    right: calc(${BREAKPOINT_PADDING_VAR} + 40px);
  }

  ${mediaBreakpointUp('desktop')} {
    width: 239px;
    top: -186px;
    right: calc(${BREAKPOINT_PADDING_VAR} + 60px);
  }
`;

const QuoteIllustrationBottom = styled.div`
  position: absolute;
  width: 236px;
  right: -28px;

  img {
    width: 100%;
  }

  ${mediaBreakpointOnly('mobile')} {
    top: calc(100% - 60px);
  }

  ${mediaBreakpointUp('tablet')} {
    bottom: -63px;
  }

  ${mediaBreakpointUp('desktop')} {
    width: 367px;
    bottom: -86px;
    right: -40px;
  }

  ${mediaBreakpointUp('desktopWide')} {
    width: 425px;
    bottom: -125px;
    right: -60px;
  }
`;

const ReviewsSection = styled.section`
  ${paddingY(19, 44)}

  h2 {
    ${customTextSize(24, 30)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    text-align: center;
    letter-spacing: -0.01em;
    color: ${COLORS.RED.LOWLIGHT};
    margin-bottom: ${remCalc(60)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(54)}

    h2 {
      ${customTextSize(38, 46)}
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(81)}

    ${Container} {
      display: grid;
      grid-template-columns: repeat(20, 1fr);
      column-gap: ${remCalc(20)};
    }

    h2 {
      ${customTextSize(49, 58)}
      grid-column: 3 / -3;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    h2 {
      grid-column: 6 / -6;
    }
  }
`;

const ReviewCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 60%;
  margin: 0 auto;
  min-width: ${remCalc(200)};

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
    width: 100%;
    margin: 0;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 1 / -1;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 4 / -4;
  }
`;

const ReviewCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${paddingY(50)}
  border-width: 0;
  border-style: solid;
  border-color: rgba(128, 128, 128, 0.5);
  border-bottom-width: 1px;

  &:last-of-type {
    border-bottom-width: 0;
    border-right-width: 0;
  }

  ${mediaBreakpointOnly('mobile')} {
    &:first-of-type {
      padding-top: 0;
    }

    &:last-of-type {
      padding-bottom: 0;
    }
  }

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(21)};
    border-bottom-width: 0;
    border-right-width: 1px;
    flex: 1 0 0;
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(30)};
  }
`;

const ReviewCardLogo = styled.div`
  margin-bottom: ${remCalc(32)};

  img {
    width: auto;
    max-height: 45px;
  }

  ${mediaBreakpointOnly('tablet')} {
    margin-bottom: ${remCalc(21)};

    img {
      max-height: 35px;
    }
  }
`;

const ReviewCardStars = styled.div`
  display: inline-flex;
  flex-direction: row;
  justify-items: flex-start;
  column-gap: ${remCalc(5)};
  margin-bottom: ${remCalc(10)};

  ${mediaBreakpointOnly('tablet')} {
    svg {
      width: ${remCalc(17)};
    }
  }
`;

const ReviewCardRating = styled.p`
  ${customTextSize(17, 26)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${mediaBreakpointOnly('tablet')} {
    ${customTextSize(12, 17)}
  }
`;

const FooterCTASection = styled.section`
  ${paddingY(100)}

  background-image: url('/staticwebassets/pages/compare/cta-bg-mobile.jpg');
  background-size: cover;
  background-position: center;

  ${Container} {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${remCalc(30)};
  }

  h3 {
    ${customTextSize(24, 30)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    text-align: center;
    letter-spacing: -0.01em;
  }

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(80)}

    background: transparent;

    h3 {
      ${customTextSize(32, 38)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(155)}

    h3 {
      ${customTextSize(49, 59)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(184)}
  }
`;

// #endregion - Components

const ComparisonPage: PageTemplate<ComparisonPageQuery> = ({ entry, globals }) => {
  const [activeFeatureId, setActiveFeatureId] = useState(entry?.featureSwitcher?.[0]?.id);

  useEffect(() => {
    setActiveFeatureId(entry?.featureSwitcher?.[0]?.id);
  }, [entry]);

  const heroImage = getSingle(entry?.heroImage);
  const activeFeature = entry?.featureSwitcher?.find((feature) => feature?.id === activeFeatureId);
  const customerQuote = getSingle(entry?.customerQuote);
  const heroCta = getSingleLink(entry?.heroCta);
  const midPageCta = getSingleLink(entry?.midPageCta);
  const footerCta = getSingleLink(entry?.footerCta);

  return (
    <PageWrapper>
      <Container>
        <HeroSection>
          <HeroHeading>
            {entry?.heroPretext && (
              <>
                <small>{entry?.heroPretext}</small>
                <VisuallyHidden>: </VisuallyHidden>
              </>
            )}

            {parseHeading(entry?.heroText)}
          </HeroHeading>

          {heroImage?.url && (
            <HeroImageWrapper>
              <Image src={heroImage?.url!} width={heroImage?.width ?? 0} height={heroImage?.height ?? 0} alt="" />
            </HeroImageWrapper>
          )}

          <HeroText>{parseHtml(entry?.heroParagraph)}</HeroText>

          {heroCta ? (
            <div>
              <Link href={heroCta.url}>
                <PrimaryInnerGlowButton
                  as="a"
                  onClick={() => {
                    gtm.track('click', {
                      element_type: 'link',
                      element_label: maybeNotSet(heroCta.text),
                      element_href: maybeNotSet(heroCta.url),
                      location: 'Hero',
                    });
                  }}
                  {...addElementIdentifier('hero-cta')}
                >
                  {heroCta.text}
                </PrimaryInnerGlowButton>
              </Link>
            </div>
          ) : null}
        </HeroSection>
      </Container>

      <HighlightsSection>
        <Container>
          <HighlightsSectionContent>
            <div>
              {entry?.differentiationBlocks?.filter(notEmpty).map((block) => (
                <HighlightCard key={block.id}>
                  <HighlightCardIcon>
                    <Image
                      src={block.icon?.[0]?.url!}
                      width={block.icon?.[0]?.width ?? 0}
                      height={block.icon?.[0]?.height ?? 0}
                      alt=""
                    />
                  </HighlightCardIcon>

                  <h4>{block.primaryTitle}</h4>
                  {parseHtml(block.text)}
                </HighlightCard>
              ))}
            </div>
          </HighlightsSectionContent>
        </Container>
      </HighlightsSection>

      <ComparisonSection>
        <Container>
          <h2>{entry?.comparisonHeading ?? ''}</h2>

          <ComparisonTableWrapper>
            <StyledComparisonTable
              columns={[
                {
                  header: (
                    <ComparisonTableHeader>
                      <ComparisonTableLogo>
                        <FrontLogoIcon />
                      </ComparisonTableLogo>

                      <h4>Front</h4>
                      <p>{entry?.competitorDescriptionFront ?? ''}</p>
                    </ComparisonTableHeader>
                  ),
                  rows:
                    entry?.competitorFeatureComparisonTable?.filter(notEmpty).map((row) => ({
                      enabled: row?.frontEnabled ?? true,
                      text: row?.front ?? '',
                    })) ?? [],
                },
                {
                  header: (
                    <ComparisonTableHeader>
                      <ComparisonTableLogo>
                        <Image
                          src={entry?.competitorLogo?.[0]?.url!}
                          width={entry?.competitorLogo?.[0]?.width ?? 0}
                          height={entry?.competitorLogo?.[0]?.height ?? 0}
                          alt=""
                        />
                      </ComparisonTableLogo>

                      <h4>{entry?.competitorName ?? ''}</h4>
                      <p>{entry?.competitorDescription ?? ''}</p>
                    </ComparisonTableHeader>
                  ),
                  rows:
                    entry?.competitorFeatureComparisonTable?.filter(notEmpty).map((row) => ({
                      enabled: row?.competitorEnabled ?? false,
                      text: row?.competitor ?? '',
                    })) ?? [],
                },
              ]}
            />
          </ComparisonTableWrapper>
        </Container>
      </ComparisonSection>

      <FeaturesSection>
        <Container>
          <FeaturesHeadline>
            <h2>{entry?.featureHeading ?? ''}</h2>
            {parseHtml(entry?.featureSubtext)}
          </FeaturesHeadline>

          <FeatureGrid>
            <FeatureImage>
              <video key={activeFeature?.id} autoPlay loop muted playsInline>
                <source src={activeFeature?.featureAsset?.[0]?.url!} />
              </video>
            </FeatureImage>

            <FeatureList>
              {entry?.featureSwitcher?.map((feature) => (
                <FeatureListItem key={feature?.id} isActive={activeFeatureId === feature?.id}>
                  <button
                    onClick={() => {
                      setActiveFeatureId(feature?.id);

                      gtm.track('click', {
                        location: 'Features',
                        element_label: striptags(maybeNotSet(feature?.title)),
                        element_type: 'button',
                      });
                    }}
                  >
                    {parseHtml(feature?.title)}
                  </button>

                  <FeatureListItemContent>{parseHtml(feature?.description)}</FeatureListItemContent>
                </FeatureListItem>
              ))}
            </FeatureList>
          </FeatureGrid>
        </Container>
      </FeaturesSection>

      {midPageCta ? (
        <MidPageCTASection>
          <Container>
            <Link href={midPageCta.url}>
              <PrimaryInnerGlowButton
                as="a"
                onClick={() => {
                  gtm.track('click', {
                    location: 'Mid-Page CTA',
                    element_label: maybeNotSet(midPageCta.text),
                    element_type: 'link',
                    element_href: maybeNotSet(midPageCta.url),
                  });
                }}
                {...addElementIdentifier('mid-page-cta')}
              >
                {midPageCta.text}
              </PrimaryInnerGlowButton>
            </Link>
          </Container>
        </MidPageCTASection>
      ) : null}

      {customerQuote && (
        <QuoteSection>
          <Container>
            <LargeQuote>
              {parseHtml(customerQuote.quote)}

              <footer>
                {!!customerQuote.authorPhoto?.length && (
                  <LargeQuoteImage>
                    <Image
                      src={customerQuote.authorPhoto?.[0]?.url!}
                      width={customerQuote.authorPhoto?.[0]?.width ?? 0}
                      height={customerQuote.authorPhoto?.[0]?.height ?? 0}
                      alt=""
                    />
                  </LargeQuoteImage>
                )}

                <div>
                  <QuoteAuthor>
                    <span>
                      <strong>{customerQuote.authorName ?? ''}</strong>
                    </span>

                    {customerQuote.authorTitle && (
                      <>
                        <VisuallyHidden>, </VisuallyHidden>
                        <span>{customerQuote.authorTitle ?? ''}</span>
                      </>
                    )}

                    {!!customerQuote.customer?.[0]?.logo?.[0] && (
                      <QuoteLogo>
                        <Image
                          src={customerQuote.customer[0].logo[0].url!}
                          width={customerQuote.customer[0].logo[0].width ?? 0}
                          height={customerQuote.customer[0].logo[0].height ?? 0}
                          alt=""
                        />
                      </QuoteLogo>
                    )}
                  </QuoteAuthor>
                </div>
              </footer>
            </LargeQuote>
          </Container>
        </QuoteSection>
      )}

      <SecondaryQuotesSection>
        <Container>
          <h2>{entry?.quoteHeading ?? ''}</h2>

          <QuoteIllustrationTop>
            <Image src="/webassets/illustrations/woman-backwards-in-chair.svg" width={155} height={291} alt="" />
          </QuoteIllustrationTop>

          {entry?.associatedQuotes?.filter(notEmpty)?.map((quote, i) => (
            <SmallQuote key={quote?.quote ?? i}>
              {parseHtml(quote?.quote)}

              <footer>
                {!!quote.customer?.[0]?.logo?.[0] ? (
                  <QuoteLogo small>
                    <Image
                      src={quote.customer[0].logo[0].url!}
                      width={quote.customer[0].logo[0].width ?? 0}
                      height={quote.customer[0].logo[0].height ?? 0}
                      alt=""
                    />
                  </QuoteLogo>
                ) : (
                  quote.customer?.[0]?.title ?? ''
                )}
              </footer>
            </SmallQuote>
          ))}

          <QuoteIllustrationBottom>
            <Image src="/webassets/illustrations/remote-work.svg" width={456} height={462} alt="" />
          </QuoteIllustrationBottom>
        </Container>
      </SecondaryQuotesSection>

      <ReviewsSection>
        <Container>
          <h2>{entry?.ratingsHeading ?? ''}</h2>

          <ReviewCardWrapper>
            {globals?.ratingCards?.filter(notEmpty).map((card, i) => {
              const [rating, stars] = (card.stars?.split('/') ?? '5/5') as [string, string];

              return (
                <ReviewCard key={`review-card-${i}`}>
                  {card.logo?.[0]?.url && (
                    <ReviewCardLogo>
                      <Image
                        src={card.logo?.[0]?.url!}
                        width={card.logo?.[0]?.width!}
                        height={card.logo?.[0]?.height!}
                        alt=""
                      />
                    </ReviewCardLogo>
                  )}

                  <ReviewCardStars>
                    {Array(parseInt(stars))
                      .fill('')
                      .map((s, i) => (
                        <Star key={`review-card-star-${i}`} width={26} fillPercent={Math.max(0, Math.min(1, parseFloat(rating) - i))} />
                      ))}
                  </ReviewCardStars>

                  <ReviewCardRating>{card.stars}</ReviewCardRating>
                </ReviewCard>
              );
            })}
          </ReviewCardWrapper>
        </Container>
      </ReviewsSection>

      {footerCta ? (
        <FooterCTASection>
          <Container>
            <h3>{entry?.footerCtaHeading ?? ''}</h3>

            <Link href={footerCta.url}>
              <PrimaryInnerGlowButton
                as="a"
                onClick={() => {
                  gtm.track('click', {
                    location: 'Footer CTA',
                    element_label: maybeNotSet(footerCta.text),
                    element_type: 'link',
                    element_href: maybeNotSet(footerCta.url),
                  });
                }}
                {...addElementIdentifier('footer-cta')}
              >
                {footerCta.text}
              </PrimaryInnerGlowButton>
            </Link>
          </Container>
        </FooterCTASection>
      ) : null}
    </PageWrapper>
  );
};

ComparisonPage.template = MasterTemplate;

export default ComparisonPage;

export const getStaticProps = getStaticPageProps<{}, { slug: string }>(
  (params) => `compare/${params!.slug}`,
  async ({ client, uri }) => {
    const data = await client.request<ComparisonPageQuery, ComparisonPageQueryVariables>(ComparisonPageQueryDocument, {
      uri,
    });

    if (!data.entry) {
      return { notFound: true };
    }

    return {
      props: {
        ...data,
      },
    };
  },
);

export const getStaticPaths = getStaticPagePaths('compare', async (context) => {
  const client = new CraftClient();

  const data = await client.request<ComparisonPathsQuery>(ComparisonPathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: `front-vs-${path?.slug!}`,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});
